import { Message } from 'lib-shared-legacy-types';

import { ConversationAuditLogEntry } from 'apis/auditLogs';
import { ConversationChannelUpdated } from 'components/activityLog/components/activityTypes/conversationChannelUpdated';
import { MessageRedacted } from 'components/activityLog/components/activityTypes/messageRedacted';

import { RoutedThroughChatbotToolCalled } from './chatbotToolCalled/chatbotToolCalled';
import { ConversationResolvedStateToggled } from './conversationResolvedStateToggled';
import { EndUserReplaced } from './endUserReplaced';
import { RoutedThroughAutomation } from './routedThroughAutomation';

import {
  AutoreplySent,
  ConversationAssigned,
  ConversationClaimed,
  ConversationClosed,
  ConversationCreated,
  ConversationCustomAttributesUpdated,
  ConversationFollowup,
  ConversationLanguageUpdated,
  ConversationLinked,
  ConversationOffered,
  ConversationOfferResponse,
  ConversationOfferTimeout,
  ConversationPending,
  ConversationRated,
  ConversationReopened,
  ConversationReserved,
  ConversationSlaEvaluation,
  ConversationSubjectUpdated,
  ConversationTagged,
  ConversationTranscriptionEnded,
  ConversationTranscriptionStarted,
  ConversationTransferred,
  ConversationUnassigned,
  MessageAdded,
  NoteAdded,
  RatingCancelled,
  RatingNotOffered,
  RatingOffered,
  RatingScheduled,
  RoutedThroughFlow,
} from './';

interface ComponentByLogTypeProps {
  entry: ConversationAuditLogEntry;
  messages: Array<Message>;
}

/**
 * Represents a component that based on the log entry type renders a component
 * for that type.
 */
export function ComponentByLogType({
  entry,
  messages,
}: ComponentByLogTypeProps): JSX.Element | null {
  switch (entry.activity_type) {
    case 'CONVERSATION_CREATED_BY_CUSTOMER':
    case 'CONVERSATION_CREATED_BY_AGENT':
      return <ConversationCreated entry={entry} />;
    case 'CONVERSATION_CLOSED':
      return <ConversationClosed entry={entry} />;
    case 'CONVERSATION_REOPENED':
      return <ConversationReopened entry={entry} />;
    case 'CONVERSATION_PENDING':
      return <ConversationPending entry={entry} />;
    case 'TAG_ADDED':
    case 'TAG_REMOVED':
      return <ConversationTagged entry={entry} />;
    case 'CONVERSATION_OFFERED':
      return <ConversationOffered entry={entry} />;
    case 'NOTE_ADDED':
      return <NoteAdded entry={entry} />;
    case 'CONVERSATION_ASSIGNED':
      return <ConversationAssigned entry={entry} />;
    case 'TRANSFER_INITIATED':
    case 'TRANSFER_FAILED':
    case 'TRANSFER_SUCCESSFUL':
      return <ConversationTransferred entry={entry} />;
    case 'MESSAGE_ADDED_BY_AGENT':
    case 'MESSAGE_ADDED_BY_CUSTOMER':
      return <MessageAdded entry={entry} messages={messages} />;
    case 'CONVERSATION_OFFER_ACCEPTED':
    case 'CONVERSATION_OFFER_REJECTED':
      return <ConversationOfferResponse entry={entry} />;
    case 'CONVERSATION_OFFER_TIMEOUT':
      return <ConversationOfferTimeout entry={entry} />;
    case 'CONVERSATION_LANGUAGE_UPDATED':
      return <ConversationLanguageUpdated entry={entry} />;
    case 'CONVERSATION_RATING_SCHEDULED':
    case 'CONVERSATION_RATING_UNSCHEDULED':
      return <RatingScheduled entry={entry} />;
    case 'CONVERSATION_RATING_OFFERED':
      return <RatingOffered entry={entry} />;
    case 'CONVERSATION_RATING_NOT_OFFERED':
      return <RatingNotOffered entry={entry} />;
    case 'CONVERSATION_RATED':
      return <ConversationRated entry={entry} />;
    case 'CONVERSATION_RATING_CANCELLED':
      return <RatingCancelled entry={entry} />;
    case 'FOLLOWUP_ADDED':
    case 'FOLLOWUP_EXPIRED':
    case 'FOLLOWUP_REMOVED':
      return <ConversationFollowup entry={entry} />;
    case 'CONVERSATION_CLAIMED':
      return <ConversationClaimed entry={entry} />;
    case 'CONVERSATION_AUTOREPLY_SENT':
      return <AutoreplySent entry={entry} />;
    case 'CONVERSATION_UNASSIGNED':
      return <ConversationUnassigned entry={entry} />;
    case 'CONVERSATION_ROUTED_THROUGH_FLOW':
      return <RoutedThroughFlow entry={entry} />;
    case 'CONVERSATION_END_USER_REPLACED':
      return <EndUserReplaced entry={entry} />;
    case 'CONVERSATION_RESERVED':
      return <ConversationReserved entry={entry} />;
    case 'CONVERSATION_SUBJECT_UPDATED':
      return <ConversationSubjectUpdated entry={entry} />;
    case 'AUTOMATION_STARTED':
      if (
        entry.event_trigger?.type !== 'mim_task' &&
        entry.event_trigger?.type !== 'mim_handoff'
      ) {
        return <RoutedThroughAutomation entry={entry} />;
      }
      return null;
    case 'CHATBOT_TOOL_CALLED':
      return <RoutedThroughChatbotToolCalled entry={entry} />;
    case 'CONVERSATION_LINKED_FROM_CHILD':
    case 'CONVERSATION_UNLINKED_FROM_CHILD':
    case 'CONVERSATION_LINKED_TO_PARENT':
    case 'CONVERSATION_UNLINKED_TO_PARENT':
      return <ConversationLinked entry={entry} />;
    case 'CONVERSATION_MESSAGE_REDACTED':
      return <MessageRedacted entry={entry} />;
    case 'CONVERSATION_RESOLVED_STATE_TOGGLED':
      return <ConversationResolvedStateToggled entry={entry} />;
    case 'CONVERSATION_CHANNEL_CHANGED':
      return <ConversationChannelUpdated entry={entry} />;
    case 'CONVERSATION_TRANSCRIPTION_STARTED':
      return <ConversationTranscriptionStarted entry={entry} />;
    case 'CONVERSATION_TRANSCRIPTION_ENDED':
      return <ConversationTranscriptionEnded entry={entry} />;
    case 'CONVERSATION_CUSTOM_ATTRIBUTES_UPDATED':
      return <ConversationCustomAttributesUpdated entry={entry} />;
    case 'CONVERSATION_SLA_EVALUATION':
      return <ConversationSlaEvaluation entry={entry} />;
    default:
      console.warn(
        `Activity Log entry of type ${entry.activity_type} is not supported.`,
      );
      return null;
  }
}
